import React, { useState, useMemo } from 'react'
import { FinanceWithdrawalAccountBlacklist, PaginationRes } from '@golden/gdk-admin'
import Box from '@material-ui/core/Box'
import CoreTable from '../../default/present/CoreTable'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import { searchToRequest } from '../../../views/admin/finance/FinanceWithdrawalAccountBlacklistPage'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { useRequestFromSearch, useGetDataByPayload, usePaginationClickAndChangeUrl, useReload } from '../../../utils/default/ComplexFlowHook'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import { createDefaultPaginationData, createTableData } from '../../../utils/default/TableHelper'
import allRoute from '../route/route'
import FinanceWithdrawalAccountBlacklistButtons from './FinanceWithdrawalAccountBlacklistButtons'
import { useChecker } from '../../../utils/admin/AdminRouteHook'
import DateTime from '../../default/present/DateTime'
import ScrollablePaper from '../../default/present/ScrollablePaper'

interface RowType {
  id: number
  createdAt: React.ReactElement
  playerName: string
  account: string
  playerAccount: string
  createdBy: string
  memo: string
  functions: React.ReactElement
}

const FinanceWithdrawalAccountBlacklistTable: React.FC = () => {
  const classes = useCommonStyles()
  const pageFlow = usePageFlow()
  const gdk = useGDK()
  const { t } = useT()
  const [list, setList] = useState<PaginationRes<FinanceWithdrawalAccountBlacklist[]>>(
    createDefaultPaginationData([])
  )
  const { reloadFlag, reload } = useReload()
  const writable = useChecker()

  const request = useRequestFromSearch({ searchToRequest })
  useGetDataByPayload({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    payload: request!,
    gdkFunc: (payload) => gdk.finance.getWithdrawalAccountBlacklist(payload),
    gdkFuncDependencies: [gdk, reloadFlag],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res: PaginationRes<FinanceWithdrawalAccountBlacklist[]>) => {
      setList(res)
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: request !== undefined
  })

  const tableClasses = useMemo(() => ({
    head: classes.pinkTableHead,
    row: classes.tableRow,
    cellHead: classes.tableCellHead
  }), [classes])
  const rows: RowType[] = useMemo(() => {
    return list.data.map((item) => ({
      id: item.id,
      createdAt: (<DateTime time={item.created_at} />),
      playerName: item.player_name,
      account: item.account,
      playerAccount: item.player_account,
      createdBy: item.created_by,
      memo: item.memo,
      functions: (
        <FinanceWithdrawalAccountBlacklistButtons
          id={item.id}
          playerName={item.player_name}
          account={item.account}
          reload={reload}
        />
      )
    }))
  }, [list.data, reload])
  const data = useMemo(() => {
    return createTableData<RowType>(
      {
        id: {
          label: '',
          value: 'id'
        },
        createdAt: {
          label: t('common.createdAt'),
          value: 'createdAt',
          align: 'center'
        },
        playerName: {
          label: t('common.realName'),
          value: 'playerName',
          align: 'center'
        },
        playerAccount: {
          label: t('common.playerAccount'),
          value: 'playerAccount',
          align: 'center'
        },
        account: {
          label: t('common.alipayAccount'),
          value: 'account',
          align: 'center'
        },
        memo: {
          label: t('common.memo'),
          value: 'memo',
          align: 'left'
        },
        createdBy: {
          label: t('common.createdBy'),
          value: 'createdBy',
          align: 'center'
        },
        functions: {
          label: t('common.function'),
          value: 'functions',
          align: 'center'
        }
      },
      writable
        ? [
            'createdAt',
            'playerName',
            'account',
            'playerAccount',
            'createdBy',
            'memo',
            'functions'
          ]
        : [
            'createdAt',
            'playerName',
            'account',
            'playerAccount',
            'createdBy',
            'memo'
          ],
      rows,
      'id'
    )
  }, [rows, t, writable])

  const handlePagination = usePaginationClickAndChangeUrl({
    request,
    encodePath: allRoute.financeWithdrawalAccountBlacklist.encodePath
  })

  if (request === undefined) return null

  return (
    <ScrollablePaper marginX={6}>
      <Box padding={4}>
        <LoadingAndErrorFrame { ...pageFlow.status }>
          <CoreTable
            classes={tableClasses}
            data={data}
            total={list.total}
            showPagination
            page={request.page}
            onChangePage={handlePagination}
          />
        </LoadingAndErrorFrame>
      </Box>
    </ScrollablePaper>
  )
}

export default React.memo(FinanceWithdrawalAccountBlacklistTable)
